import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, IconButton, TextField } from '@mui/material';
import type { CSSProperties } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import Button from '../Button/Button';
import Text from '../Text/Text';
import ToolTip from '../ToolTip/ToolTip';

const addRowButtonStyle: CSSProperties = {
  textTransform: 'none',
  height: 'auto',
  minHeight: '36px',
  gap: '0.35rem',
  borderColor: 'var(--primary-color)',
  color: 'var(--primary-color)',
  backgroundColor: '#FFFFFF',
  fontSize: '0.8125rem',
  letterSpacing: '0.01em',
};

// Registers webscrap_exclude_url_patterns as an array of { value } objects -
// useFieldArray needs object entries for stable keys. Each value is a plain
// URL the user types in, escaped into a regex in buildWebscrapPayload before
// it reaches the API, since the backend's exclude_url_patterns field only
// accepts regex strings.
const WebscrapExcludePatterns = () => {
  const { t } = useTranslation();
  const { control, register } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'webscrap_exclude_url_patterns',
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <Text type="label">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {t('Exclude URLs')}
            <ToolTip
              title={t(
                'Skip scraping any page whose URL contains one of these.'
              )}
            />
          </Box>
        </Text>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="small"
          padding="0.35rem 0.85rem"
          style={addRowButtonStyle}
          onClick={() => append({ value: '' })}
        >
          <Add sx={{ fontSize: '1rem' }} />
          {t('Add URL')}
        </Button>
      </Box>

      {fields.map((field, index) => (
        <Box
          key={field.id}
          sx={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            marginTop: '0.625rem',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="https://example.com/tag/"
            inputProps={{
              'aria-label': t('Exclude URL {{number}}', {
                number: index + 1,
              }),
            }}
            {...register(`webscrap_exclude_url_patterns.${index}.value`)}
          />
          <IconButton
            aria-label={t('Remove URL')}
            onClick={() => remove(index)}
            size="small"
            type="button"
            sx={{ color: '#94A3B8' }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

export default WebscrapExcludePatterns;
